<template>
  <div class="buttonSelect" :class="{ isFix: !scroll }">
    <Button v-if="scroll" class="prev" :icon="ArrowIcon" :disabled="isPrevDisabled" @click="prev" />
    <div class="wrapper">
      <ul ref="list" class="list" :style="{ transform: `translateX(-${position * 88}px)` }">
        <Button
          v-for="{ value, label } in list"
          :key="value"
          class="item"
          :primary="value == current"
          :disabled="disabledValues.includes(value)"
          @click="handleSelect(value)"
        >
          <!-- @slot 選択肢の書き換え -->
          <slot :value="value" :label="label">{{ label }}</slot>
        </Button>
      </ul>
    </div>
    <Button v-if="scroll" class="next" :icon="ArrowIcon" :disabled="isNextDisabled" @click="next" />
  </div>
</template>

<script>
import { isEqual, debounce } from 'lodash'
import Button from '@/components/atoms/Button'
import ArrowIcon from '@/components/icons/arrow'

/**
 * ボタン選択
 */
export default {
  name: 'ButtonSelect',
  components: {
    Button,
  },
  model: {
    prop: 'current',
    event: 'select',
  },
  props: {
    /** 選択肢 */
    items: { type: [Object, Array, Map], default: null },
    /** 選択中の値 (v-model) */
    current: { type: [Number, String], default: null },
    /** 非活性にする値の配列 */
    disabledValues: { type: Array, default: () => [] },
    /** ページ送り */
    scroll: Boolean,
  },
  data() {
    return {
      position: 0,
      ArrowIcon,
    }
  },
  computed: {
    list() {
      if (this.items instanceof Array) return this.items.map(value => ({ value, label: value }))
      if (this.items instanceof Object) return Object.entries(this.items).map(([value, label]) => ({ value, label }))
      return []
    },
    itemsLength() {
      return this.list.length
    },
    isPrevDisabled() {
      return this.position === 0 || this.itemsLength < 6
    },
    isNextDisabled() {
      return this.position === this.itemsLength - 6 || this.itemsLength <= 6
    },
  },
  watch: {
    items(newVal, oldVal) {
      if (!isEqual(newVal, oldVal)) {
        this.position = 0
      }
    },
  },
  created() {
    this.resetPosition = debounce(() => {
      if (getComputedStyle(this.$refs.list).getPropertyValue('position') !== 'absolute') {
        this.position = 0
      }
    }, 200)
    window.addEventListener('resize', this.resetPosition)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resetPosition)
  },
  methods: {
    prev() {
      this.position = Math.max(this.position - 6, 0)
    },
    next() {
      this.position = Math.min(this.position + 6, this.itemsLength - 6)
    },
    handleSelect(key) {
      /**
       * 選択肢の選択 (v-model)
       * @event select
       * @type *
       */
      this.$emit('select', key)
    },
  },
}
</script>

<style lang="postcss" scoped>
.buttonSelect {
  align-items: center;
  box-sizing: border-box;
  display: flex;
  margin: -4px;
  overflow: hidden;
  padding: 0;
  position: relative;

  @media screen and (min-width: 768px) {
    padding: 0 4px;
    width: 100%;
  }
}

.wrapper {
  flex: 1 1 auto;
  height: 59px;
  overflow: hidden;
  position: relative;

  @media screen and (min-width: 768px) {
    margin: 0 14px;
    overflow: hidden;
  }
}

.list {
  display: flex;
  justify-content: flex-start;
  left: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  padding: 4px;
  scroll-snap-type: x mandatory;
  transition: transform 0.3s;

  &::-webkit-scrollbar {
    display: none;
  }

  @media screen and (min-width: 768px) {
    overflow: visible;
    position: absolute;
  }
}

.item {
  border-color: var(--form-border-color);
  flex-shrink: 0;
  font-size: 14px;
  height: 48px;
  padding: 0;
  scroll-snap-align: start;
  width: 72px;

  & + .item {
    margin-left: 8px;
  }

  @media screen and (min-width: 768px) {
    width: 80px;
  }
}

.item + .item {
  @media all and (-ms-high-contrast: none) {
    margin-left: 8px;
  }
}

.prev,
.next {
  padding: 0;

  @media screen and (max-width: 767px) {
    display: none;
  }
}

.prev {
  transform: rotate(180deg);
}

.isFix {
  & .wrapper {
    margin: 0;
  }

  @media screen and (min-width: 767px) {
    & .item {
      width: 94px;
    }
  }
}
</style>
